import { skipToken, useQuery } from "@tanstack/react-query";
import {
  type GetChatTokenQueryResult,
  getChatToken,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const CHAT_TOKEN_QUERY_KEY = "CHAT_TOKEN";

export type ChatTokenData = GetChatTokenQueryResult;

export const chatTokenKeys = {
  all: [CHAT_TOKEN_QUERY_KEY],
  byIdentityUser: (activeIdentityUserPublicID?: string | null) => [
    ...chatTokenKeys.all,
    activeIdentityUserPublicID,
  ],
};

const fetchChatTokenRequest = processResponseWithApiProblemDetails(() =>
  getChatToken(),
);

/**
 * Always hits the API (never the query cache) — the Stream token provider must get a token that
 * is fresh at call time, not one aged by the query's staleTime.
 */
export const fetchChatToken = (): Promise<ChatTokenData> =>
  fetchChatTokenRequest();

/**
 * Chat identities are IdentityUser-keyed — the query key includes the active IdentityUser's
 * PublicID so a context switch busts the cached token and Stream reconnects as the new persona.
 * Non-migrated users (no active IdentityUser) never fetch: chat is not available for them.
 */
export const useChatTokenQuery = (
  isAuthenticated: boolean,
  activeIdentityUserPublicID?: string | null,
) =>
  useQuery<ChatTokenData>({
    queryKey: chatTokenKeys.byIdentityUser(activeIdentityUserPublicID),
    queryFn:
      isAuthenticated && activeIdentityUserPublicID != null
        ? () => fetchChatToken()
        : skipToken,
    staleTime: MS_TIME.halfHour,
    // Consumers render their own unavailable states (chat page) or silently skip (the
    // always-mounted unread badge). Reporting follows the global rule: 4xx is a breadcrumb,
    // 5xx a Sentry event.
  });
